/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Cell, FormControl, Grid, HFlow, Text, Tooltip, useTheme, VFlow } from 'bold-ui'
import { ErrorField, RadioField, SubmitButton, SwitchField, TextAreaField } from 'components/form'
import { CiapSelectField, CiapSelectFieldModel } from 'components/form/field/select/CiapSelectField/CiapSelectField'
import { CidSelectField } from 'components/form/field/select/CidSelectField/CidSelectField'
import {
  ProblemaCondicaoSelectField,
  ProblemaCondicaoSelectFieldModel,
} from 'components/form/field/select/ProblemaCondicaoSelectField/ProblemaCondicaoSelectField'
import { anyFieldIsTouched } from 'components/form/final-form/util'
import { CiapCapituloEnum, SituacaoProblema } from 'graphql/types.generated'
import { Fragment, useMemo } from 'react'
import { isEmpty } from 'util/validation/Util'
import {
  EditableListForm,
  EditableListFormProps,
  EditableListFormRenderProps,
} from 'view/atendimentos/detail/components/EditableListForm'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { createProblemaCondicoesCalculator } from './calculator'
import { DataProblemaField } from './DataProblemaField'
import { ProblemaCondicaoModel } from './model'
import { problemasCondicoesValidator } from './validator'

export interface ProblemasCondicoesFormProps
  extends Pick<EditableListFormProps<ProblemaCondicaoModel>, 'initialValues' | 'onSubmit'> {
  edicao?: boolean
  somenteCiap?: boolean
  formValues?: Array<ProblemaCondicaoModel>
  prontuarioId: ID

  onCancel?(): void

  cidadao?: CidadaoAtendimento
  dataAtendimento?: Instant
  problemasCidadao?: ProblemaCondicaoSelectFieldModel[]
}

export function ProblemasCondicoesForm(props: ProblemasCondicoesFormProps) {
  const {
    edicao,
    somenteCiap,
    formValues,
    onCancel,
    cidadao,
    prontuarioId,
    dataAtendimento,
    initialValues,
    problemasCidadao,
    ...rest
  } = props
  const theme = useTheme()

  const prefix = edicao ? initialValues._id ?? 'edit' : 'new'
  const decorators = useMemo(
    () => createProblemaCondicoesCalculator(problemasCidadao, cidadao.dataNascimento, prefix),
    [cidadao.dataNascimento, problemasCidadao, prefix]
  )

  const ciapIsEqual = (item: CiapSelectFieldModel) => {
    return !!(formValues || []).find((o2) => item.id === o2.ciap.id)
  }

  const problemaIsEqual = (item: ProblemaCondicaoSelectFieldModel) => {
    return !!(formValues || []).find(
      (o2) => item.id === o2.problemaId || (item.ciap?.id === o2.ciap?.id && item.cid10?.id === o2.cid?.id)
    )
  }

  const renderForm = ({
    form,
    values,
    error,
    handleSubmit,
    name,
  }: EditableListFormRenderProps<ProblemaCondicaoModel>) => {
    const disabledIncluirListaProblemas = !values?.ciap?.id && !values?.cid?.id

    const disabledCiapCid =
      !disabledIncluirListaProblemas && !!values?.incluirNaListaProblemas && !values?.problemaCondicaoEvoluir

    const ciapDisabled =
      (!!values?.problemaCondicaoEvoluir && !!values?.problemaCondicaoEvoluir?.ciap) || disabledCiapCid

    const cidDisabled =
      (!!values?.problemaCondicaoEvoluir && !!values?.problemaCondicaoEvoluir?.cid10) || disabledCiapCid

    return (
      <VFlow
        style={
          edicao &&
          css`
            margin-left: -1rem;
            margin-right: -1rem;
          `
        }
      >
        {!edicao && (
          <div
            css={css`
              background-color: ${theme.pallete.gray.c90};
              padding: 1rem;
              border-bottom: 1px ${theme.pallete.divider} solid;
            `}
          >
            <Grid>
              <Cell size={6}>
                <ProblemaCondicaoSelectField
                  name={name.problemaCondicaoEvoluir}
                  itemIsEqual={problemaIsEqual}
                  problemas={problemasCidadao}
                  label='Pesquisar por problemas/condições ativos ou latentes do cidadão'
                />
              </Cell>
            </Grid>
          </div>
        )}
        <div
          css={css`
            padding: 0 1rem 1rem 1rem;
          `}
        >
          {anyFieldIsTouched(form) && error && <Text color='danger'>{error}</Text>}

          <Grid>
            <Cell size={6}>
              {edicao ? (
                <FormControl label='CIAP 2'>
                  {values?.ciap ? `${values?.ciap.nome} (${values?.ciap.codigo})` : '-'}
                </FormControl>
              ) : (
                <Tooltip
                  text={
                    ciapDisabled && 'CIAP2 de problema inserido na Lista de problemas/condições não pode ser alterada.'
                  }
                >
                  <CiapSelectField
                    name={name.ciap}
                    label='CIAP 2'
                    itemIsEqual={somenteCiap && ciapIsEqual}
                    sexo={isEmpty(cidadao?.identidadeGeneroDbEnum) ? cidadao?.sexo : undefined}
                    capitulosExcluidos={[CiapCapituloEnum.PROCEDIMENTOS]}
                    excluirCIAPsDAB={true}
                    disabled={ciapDisabled}
                    menuMinWidth={620}
                  />
                </Tooltip>
              )}
            </Cell>
            {!somenteCiap || values?.problemaCondicaoEvoluir ? (
              <Fragment>
                <Cell size={6}>
                  {somenteCiap || edicao ? (
                    <FormControl label='CID 10'>
                      {values?.cid ? `${values?.cid.nome} (${values?.cid.codigo})` : '-'}
                    </FormControl>
                  ) : (
                    <Tooltip
                      text={
                        cidDisabled &&
                        'CID10 de problema inserido na Lista de problemas/condições não pode ser alterada.'
                      }
                    >
                      <CidSelectField
                        name={name.cid}
                        label='CID 10'
                        sexo={isEmpty(cidadao?.identidadeGeneroDbEnum) ? cidadao?.sexo : undefined}
                        ciapRelacionada={values?.ciap?.id}
                        disabled={cidDisabled}
                        menuMinWidth={620}
                      />
                    </Tooltip>
                  )}
                </Cell>
                {values?.problemaCondicaoEvoluir?.descricaoOutro && (
                  <Cell size={12}>
                    <FormControl label='Outro'>{values?.problemaCondicaoEvoluir?.descricaoOutro}</FormControl>
                  </Cell>
                )}
              </Fragment>
            ) : (
              values?.problemaCondicaoEvoluir?.descricaoOutro && (
                <Cell size={12}>
                  <FormControl label='Outro'>{values?.problemaCondicaoEvoluir?.descricaoOutro}</FormControl>
                </Cell>
              )
            )}
            <Cell size={12}>
              <HFlow
                justifyContent='space-between'
                style={css`
                  height: 2rem;
                `}
              >
                <Tooltip text={disabledIncluirListaProblemas && 'Necessário informar CIAP ou CID.'}>
                  <SwitchField
                    name={name.incluirNaListaProblemas}
                    label='Incluir na lista de problemas/condições'
                    disabled={disabledIncluirListaProblemas}
                  />
                </Tooltip>
                {!values?.incluirNaListaProblemas && (
                  <Buttons edicao={edicao} onSubmit={handleSubmit} onCancel={onCancel} />
                )}
              </HFlow>
            </Cell>
          </Grid>
          {values?.incluirNaListaProblemas && (
            <Grid>
              <Cell
                size={12}
                style={css`
                  padding-top: 1rem;
                `}
              >
                <FormControl label='Situação' required>
                  <VFlow vSpacing={0.5}>
                    <HFlow
                      style={css`
                        margin-left: -0.5rem;
                        padding-top: 0.5rem;
                      `}
                    >
                      <RadioField name={name.situacaoProblema} label='Ativo' value={SituacaoProblema.ATIVO} />
                      <RadioField name={name.situacaoProblema} label='Latente' value={SituacaoProblema.LATENTE} />
                      <RadioField name={name.situacaoProblema} label='Resolvido' value={SituacaoProblema.RESOLVIDO} />
                    </HFlow>
                    <ErrorField name={name.situacaoProblema} />
                  </VFlow>
                </FormControl>
              </Cell>
              <Cell xs={12}>
                <DataProblemaField
                  name={name.dataInicio}
                  label='Início do problema'
                  dataNascimentoCidadao={cidadao?.dataNascimento}
                />
              </Cell>
              <Cell xs={12}>
                {values?.situacaoProblema && values?.situacaoProblema !== SituacaoProblema.ATIVO && (
                  <DataProblemaField
                    name={name.dataFim}
                    label='Fim do problema'
                    dataNascimentoCidadao={cidadao?.dataNascimento}
                  />
                )}
              </Cell>
              <Cell size={12}>
                <TextAreaField name={name.observacao} label='Observações' maxLength={200} />
              </Cell>
              <Cell size={12}>
                <Buttons edicao={edicao} onSubmit={handleSubmit} onCancel={onCancel} />
              </Cell>
            </Grid>
          )}
        </div>
      </VFlow>
    )
  }

  return (
    <EditableListForm<ProblemaCondicaoModel>
      validate={problemasCondicoesValidator(
        props.somenteCiap,
        formValues,
        edicao,
        props.dataAtendimento,
        cidadao.dataNascimento
      )}
      render={renderForm}
      prefix={prefix}
      initialValues={initialValues}
      decorators={[decorators]}
      validateOnBlur={false}
      {...rest}
    />
  )
}

interface FooterButtonsProps {
  edicao: boolean
  onSubmit: EditableListFormRenderProps<ProblemaCondicaoModel>['handleSubmit']

  onCancel(): void
}

const Buttons = (props: FooterButtonsProps) => {
  return (
    <HFlow justifyContent='flex-end'>
      {props.edicao ? (
        <Fragment>
          <Button size='small' onClick={props.onCancel}>
            Cancelar
          </Button>
          <SubmitButton size='small' handleSubmit={props.onSubmit}>
            Salvar
          </SubmitButton>
        </Fragment>
      ) : (
        <SubmitButton size='small' handleSubmit={props.onSubmit}>
          Adicionar
        </SubmitButton>
      )}
    </HFlow>
  )
}
